import React, { useState, useCallback, useEffect } from 'react';
import LoginPage from './components/LoginPage';
import RegistrationPage from './components/RegistrationPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import DashboardLayout from './components/DashboardLayout';
import { User } from './types';
import * as realApi from './services/apiService';
import { LoadingSpinner } from './components/icons';

type AuthPage = 'login' | 'register' | 'forgot';

const App: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [authPage, setAuthPage] = useState<AuthPage>('login');
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [verifyStatus, setVerifyStatus] = useState<'checking' | 'success' | 'error' | null>(null);

  useEffect(() => {
    const initApp = async () => {
        const params = new URLSearchParams(window.location.search);
        const verifyToken = params.get('verify');
        const reset = params.get('reset');

        if (verifyToken) {
            setVerifyStatus('checking');
            try {
                await realApi.verifyEmail(verifyToken);
                setVerifyStatus('success');
            } catch {
                setVerifyStatus('error');
            }
            window.history.replaceState({}, '', window.location.pathname);
        }
        if (reset) {
            setResetToken(reset);
        }

        const productionReady = await realApi.isProduction();
        if (!productionReady) {
            // No mock-data fallback - if the real backend is unreachable, say so
            // instead of silently showing invented records as if they were real.
            setServiceUnavailable(true);
            setIsLoading(false);
            return;
        }
        const storedUser = sessionStorage.getItem('currentUser');
        if (storedUser) setCurrentUser(JSON.parse(storedUser));
        setIsLoading(false);
    };
    initApp();
  }, []);

  const handleLogin = useCallback(async (email: string, password: string) => {
    setIsLoading(true); setError(null);
    try {
      const user = await realApi.login(email, password);
      setCurrentUser(user);
      sessionStorage.setItem('currentUser', JSON.stringify(user));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleLogout = useCallback(() => {
    realApi.logout().catch(() => {}); // best-effort - clear local state regardless
    setCurrentUser(null);
    sessionStorage.removeItem('currentUser');
    sessionStorage.removeItem('authToken');
  }, []);

  useEffect(() => {
    const onUnauthorized = () => handleLogout();
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, [handleLogout]);

  if (isLoading && !currentUser) return <div className="flex items-center justify-center h-screen bg-background"><LoadingSpinner className="w-12 h-12 text-primary" /></div>;

  if (serviceUnavailable) {
    return (
      <div className="flex items-center justify-center h-screen bg-background px-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Service Unavailable</h1>
          <p className="text-gray-600">TCR Portal can't reach its database right now. This isn't showing you demo data - please try again shortly, or contact your platform administrator if this continues.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {resetToken ? (
        <ResetPasswordPage token={resetToken} onDone={() => { setResetToken(null); setAuthPage('login'); }} />
      ) : !currentUser ? (
        authPage === 'register' ? <RegistrationPage onSwitchToLogin={() => setAuthPage('login')} /> :
        authPage === 'forgot' ? <ForgotPasswordPage onBackToLogin={() => setAuthPage('login')} /> :
        <LoginPage onLogin={handleLogin} error={error} isLoading={isLoading} onSwitchToRegister={() => setAuthPage('register')} onForgotPassword={() => setAuthPage('forgot')} verifyStatus={verifyStatus} />
      ) : (
        <DashboardLayout user={currentUser} onLogout={handleLogout} onUserUpdate={setCurrentUser} />
      )}
    </div>
  );
};
export default App;
